import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AddCard as AddFundsIcon } from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

export interface AutoReplenishSettings {
  enabled: boolean;
  thresholdAmount: number;
  replenishAmount: number;
  paymentSource: 'STRIPE_CARD' | 'STRIPE_ACH';
  stripePaymentMethodId?: string;
  lastReplenished?: string;
}

export interface AddFundsResult {
  success: boolean;
  message: string;
  stripePaymentId?: string;
  isDevMode?: boolean;
  newBalance?: number;
}

export interface SavedPaymentMethod {
  paymentMethodId: string;
  type: 'card' | 'bank_account';
  card?: { brand?: string; last4?: string; expMonth?: number; expYear?: number };
  bankAccount?: { bankName?: string; last4?: string; accountType?: string };
}

export interface AddFundsTabProps {
  addFunds: (
    amount: number,
    paymentMethodId?: string,
    paymentType?: string,
    devMode?: boolean,
  ) => Promise<AddFundsResult>;
  getAutoReplenish: () => Promise<AutoReplenishSettings>;
  saveAutoReplenish: (settings: AutoReplenishSettings) => Promise<{ success: boolean; message: string }>;
  listPaymentMethods: () => Promise<SavedPaymentMethod[]>;
  setupCardPaymentMethod: () => Promise<{ clientSecret: string }>;
  setupBankPaymentMethod: () => Promise<{ clientSecret: string }>;
  minimumAmount: number;
  isDevMode?: boolean;
  /**
   * Set when the account may not enter payment details yet (provisional account with no
   * verified email or phone). Card entry and deposits are disabled and the reason shown.
   */
  cardEntryBlockedReason?: string | null;
  /** Called after a deposit succeeds — used to return the user to the page that sent them here. */
  onDepositComplete?: (result: AddFundsResult) => void;
}

interface SetupIntentFormProps {
  clientSecret: string;
  label: string;
  /** When true, use CardElement (card fields only — no Link Instant Bank tile). */
  useCardElement?: boolean;
  disableWallets?: boolean;
  disabled?: boolean;
  onSuccess: (paymentMethodId: string) => void;
  onError: (message: string) => void;
}

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY?.trim() || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

function SetupIntentForm({
  clientSecret,
  label,
  useCardElement,
  disableWallets,
  disabled,
  onSuccess,
  onError,
}: SetupIntentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    try {
      if (useCardElement) {
        const card = elements.getElement(CardElement);
        if (!card) {
          onError('Card form is not ready.');
          return;
        }
        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card },
        });
        if (error) {
          onError(error.message || `Failed to save ${label}.`);
          return;
        }
        const paymentMethodId =
          typeof setupIntent?.payment_method === 'string'
            ? setupIntent.payment_method
            : (setupIntent?.payment_method as { id?: string } | null)?.id;
        if (!paymentMethodId) {
          onError('Stripe setup completed but no payment method ID was returned.');
          return;
        }
        onSuccess(paymentMethodId);
        return;
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        onError(error.message || `Failed to save ${label}.`);
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent?.payment_method as { id?: string } | null)?.id;

      if (!paymentMethodId) {
        onError('Stripe setup completed but no payment method ID was returned.');
        return;
      }

      onSuccess(paymentMethodId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Stripe Secure Setup ({label})
        {useCardElement ? ' — card only' : ''}
      </Typography>
      {useCardElement ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1 }}>
          <CardElement
            options={{
              disableLink: true,
              hidePostalCode: false,
            } as any}
          />
        </Box>
      ) : (
        <PaymentElement
          options={{
            layout: 'tabs',
            wallets: {
              applePay: disableWallets ? 'never' : 'auto',
              googlePay: disableWallets ? 'never' : 'auto',
              link: 'never',
            },
          }}
        />
      )}
      {useCardElement && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Bank accounts are added under Bank Transfer (ACH) only — not here.
        </Typography>
      )}
      {!useCardElement && label.toLowerCase().includes('bank') && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          ACH Direct Debit (~0.8% fee). Funds stay reserved until the bank confirms (1–5 business days).
        </Typography>
      )}
      <Button
        sx={{ mt: 2 }}
        variant="outlined"
        onClick={handleConfirm}
        disabled={disabled || saving || !stripe || !elements}
      >
        {saving ? `Saving ${label}...` : `Save ${label}`}
      </Button>
    </Box>
  );
}

const AddFundsTab: React.FC<AddFundsTabProps> = ({
  addFunds,
  getAutoReplenish,
  saveAutoReplenish,
  listPaymentMethods,
  setupCardPaymentMethod,
  setupBankPaymentMethod,
  minimumAmount,
  isDevMode: isDevModeOverride,
  cardEntryBlockedReason,
  onDepositComplete,
}) => {
  const cardEntryBlocked = !!cardEntryBlockedReason;
  // Localhost no longer auto-bypasses Stripe. Set VITE_ADD_FUNDS_DEV_BYPASS=true
  // only when you intentionally want the no-charge Add Credits path.
  const isDevMode =
    isDevModeOverride ??
    String(import.meta.env.VITE_ADD_FUNDS_DEV_BYPASS || '')
      .trim()
      .toLowerCase() === 'true';
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const disableWallets = hostname !== 'mail.qixit.com';

  const [addFundsAmount, setAddFundsAmount] = useState<string>('');
  const [addFundsPaymentMethod, setAddFundsPaymentMethod] = useState<'CARD' | 'ACH'>('CARD');
  const [addFundsPaymentMethodId, setAddFundsPaymentMethodId] = useState<string>('');
  const [addFundsLoading, setAddFundsLoading] = useState(false);
  const [addFundsResult, setAddFundsResult] = useState<{ success: boolean; message: string } | null>(null);

  const defaultArSettings: AutoReplenishSettings = {
    enabled: false,
    thresholdAmount: minimumAmount * 2,
    replenishAmount: minimumAmount * 5,
    paymentSource: 'STRIPE_CARD',
  };
  const [arSettings, setArSettings] = useState<AutoReplenishSettings>(defaultArSettings);
  const [arLoading, setArLoading] = useState(false);
  const [arSaveResult, setArSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  const [savedMethods, setSavedMethods] = useState<SavedPaymentMethod[]>([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string>('');
  const [setupFor, setSetupFor] = useState<'CARD' | 'ACH' | null>(null);

  const stripeNotConfigured = !isDevMode && !stripePublishableKey;

  const loadPaymentMethods = async () => {
    if (isDevMode) return;
    setMethodsLoading(true);
    try {
      setSavedMethods(await listPaymentMethods());
    } catch {
      // Non-blocking; user can still attempt setup flow.
    } finally {
      setMethodsLoading(false);
    }
  };

  useEffect(() => {
    getAutoReplenish()
      .then((s) => setArSettings(s))
      .catch(() => {
        // ignore
      });
    void loadPaymentMethods();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cardMethods = useMemo(
    () => savedMethods.filter((m) => m.type === 'card'),
    [savedMethods],
  );
  const achMethods = useMemo(
    () => savedMethods.filter((m) => m.type === 'bank_account'),
    [savedMethods],
  );
  const availableForAddFunds = addFundsPaymentMethod === 'CARD' ? cardMethods : achMethods;
  const availableForAuto = arSettings.paymentSource === 'STRIPE_CARD' ? cardMethods : achMethods;

  // Prefill Add Funds method with a saved method matching selected type.
  useEffect(() => {
    if (isDevMode) return;
    const methodPool = addFundsPaymentMethod === 'CARD' ? cardMethods : achMethods;
    if (methodPool.length === 0) {
      setAddFundsPaymentMethodId('');
      return;
    }
    const selectedStillValid = methodPool.some((m) => m.paymentMethodId === addFundsPaymentMethodId);
    if (!selectedStillValid) {
      setAddFundsPaymentMethodId(methodPool[0].paymentMethodId);
    }
  }, [isDevMode, addFundsPaymentMethod, cardMethods, achMethods, addFundsPaymentMethodId]);

  // Prefill auto-replenish method with a saved method matching selected type.
  useEffect(() => {
    if (isDevMode) return;
    const methodPool = arSettings.paymentSource === 'STRIPE_CARD' ? cardMethods : achMethods;
    const selectedId = arSettings.stripePaymentMethodId || '';
    if (methodPool.length === 0) {
      if (selectedId) {
        setArSettings((s) => ({ ...s, stripePaymentMethodId: '' }));
      }
      return;
    }
    const selectedStillValid = methodPool.some((m) => m.paymentMethodId === selectedId);
    if (!selectedStillValid) {
      setArSettings((s) => ({ ...s, stripePaymentMethodId: methodPool[0].paymentMethodId }));
    }
  }, [isDevMode, arSettings.paymentSource, arSettings.stripePaymentMethodId, cardMethods, achMethods]);

  const openSetup = async (target: 'CARD' | 'ACH') => {
    if (stripeNotConfigured) return;
    if (cardEntryBlocked) {
      setAddFundsResult({ success: false, message: cardEntryBlockedReason! });
      return;
    }
    setSetupFor(target);
    setSetupClientSecret('');
    try {
      const result = target === 'CARD'
        ? await setupCardPaymentMethod()
        : await setupBankPaymentMethod();
      if (!result?.clientSecret) {
        setAddFundsResult({ success: false, message: 'Failed to initialize Stripe setup.' });
        return;
      }
      setSetupClientSecret(result.clientSecret);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string; message?: string } }; message?: string })?.response?.data?.error ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Failed to initialize Stripe setup.';
      setAddFundsResult({ success: false, message: msg });
    }
  };

  const handleSetupSuccess = async (paymentMethodId: string) => {
    setSetupClientSecret('');
    setSetupFor(null);
    await loadPaymentMethods();
    setAddFundsPaymentMethodId(paymentMethodId);
    setArSettings((s) => ({ ...s, stripePaymentMethodId: paymentMethodId }));
    setAddFundsResult({ success: true, message: `Payment method saved (${paymentMethodId}).` });
  };

  const handleSetupError = (message: string) => {
    setAddFundsResult({ success: false, message });
  };

  const handleAddFunds = async () => {
    if (cardEntryBlocked) {
      setAddFundsResult({ success: false, message: cardEntryBlockedReason! });
      return;
    }
    const amount = parseFloat(addFundsAmount);
    if (isNaN(amount) || amount < minimumAmount) {
      setAddFundsResult({ success: false, message: `Minimum is $${minimumAmount.toFixed(2)}.` });
      return;
    }
    if (!isDevMode && !addFundsPaymentMethodId) {
      setAddFundsResult({ success: false, message: 'Select or add a Stripe payment method first.' });
      return;
    }

    setAddFundsLoading(true);
    setAddFundsResult(null);
    try {
      const result = await addFunds(amount, addFundsPaymentMethodId || undefined, addFundsPaymentMethod, isDevMode);
      setAddFundsResult({ success: result.success, message: result.message });
      if (result.success) {
        setAddFundsAmount('');
        onDepositComplete?.(result);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Payment failed. Please try again.';
      setAddFundsResult({ success: false, message: msg });
    } finally {
      setAddFundsLoading(false);
    }
  };

  const handleSaveAutoReplenish = async () => {
    if (arSettings.enabled && cardEntryBlocked) {
      setArSaveResult({ success: false, message: cardEntryBlockedReason! });
      return;
    }
    if (arSettings.enabled && arSettings.replenishAmount < minimumAmount) {
      setArSaveResult({ success: false, message: `Minimum replenish amount is $${minimumAmount.toFixed(2)}.` });
      return;
    }
    if (arSettings.enabled && !isDevMode && !arSettings.stripePaymentMethodId) {
      setArSaveResult({ success: false, message: 'Select or add a Stripe payment method for auto-replenish.' });
      return;
    }
    setArLoading(true);
    setArSaveResult(null);
    try {
      setArSaveResult(await saveAutoReplenish(arSettings));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Failed to save settings.';
      setArSaveResult({ success: false, message: msg });
    } finally {
      setArLoading(false);
    }
  };

  const parsedAmount = parseFloat(addFundsAmount);
  const amountTooLow = !!addFundsAmount && parsedAmount < minimumAmount;
  const addFundsDisabled =
    addFundsLoading ||
    cardEntryBlocked ||
    !addFundsAmount ||
    amountTooLow ||
    stripeNotConfigured ||
    (!isDevMode && !addFundsPaymentMethodId);

  return (
    <Box sx={{ maxWidth: 700 }}>
      {isDevMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Developer Mode Active</strong> — Stripe charges are bypassed.
        </Alert>
      )}

      {cardEntryBlocked && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Verify your contact details first</strong> — {cardEntryBlockedReason}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Add Credits</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Minimum: <strong>${minimumAmount.toFixed(2)}</strong>
        </Typography>

        <TextField
          label="Amount ($)"
          type="number"
          fullWidth
          value={addFundsAmount}
          onChange={(e) => {
            setAddFundsAmount(e.target.value);
            setAddFundsResult(null);
          }}
          inputProps={{ min: minimumAmount, step: '1' }}
          helperText={amountTooLow ? `Minimum is $${minimumAmount.toFixed(2)}` : `Minimum: $${minimumAmount.toFixed(2)}`}
          error={amountTooLow}
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Payment Method Type</Typography>
          <ToggleButtonGroup
            value={addFundsPaymentMethod}
            exclusive
            size="small"
            onChange={(_, v) => {
              if (!v) return;
              setAddFundsPaymentMethod(v);
              setAddFundsPaymentMethodId('');
              setSetupClientSecret('');
              setSetupFor(null);
            }}
          >
            <ToggleButton value="CARD">Credit / Debit Card</ToggleButton>
            <ToggleButton value="ACH">Bank Transfer (ACH)</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {!isDevMode && (
          <>
            {stripeNotConfigured && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Stripe publishable key is missing (`VITE_STRIPE_PUBLISHABLE_KEY`).
              </Alert>
            )}

            <TextField
              select
              fullWidth
              label="Saved Stripe Method"
              value={addFundsPaymentMethodId}
              onChange={(e) => setAddFundsPaymentMethodId(e.target.value)}
              sx={{ mb: 2 }}
              helperText={methodsLoading ? 'Loading methods...' : 'Select an existing method or add a new one.'}
            >
              {availableForAddFunds.map((m) => (
                <MenuItem key={m.paymentMethodId} value={m.paymentMethodId}>
                  {m.type === 'card'
                    ? `${m.card?.brand?.toUpperCase() || 'CARD'} •••• ${m.card?.last4 || '----'}`
                    : `${m.bankAccount?.bankName || 'Bank'} •••• ${m.bankAccount?.last4 || '----'}`}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                disabled={stripeNotConfigured || cardEntryBlocked}
                onClick={() => void openSetup(addFundsPaymentMethod)}
              >
                {addFundsPaymentMethod === 'CARD' ? 'Add New Card' : 'Add New Bank Account'}
              </Button>
              <Button variant="text" onClick={() => void loadPaymentMethods()} disabled={methodsLoading}>
                {methodsLoading ? 'Refreshing...' : 'Refresh Methods'}
              </Button>
            </Box>

            {setupClientSecret && setupFor === addFundsPaymentMethod && (
              <Elements
                stripe={stripePromise}
                options={
                  setupFor === 'CARD'
                    ? { appearance: { theme: 'stripe' } }
                    : { clientSecret: setupClientSecret, appearance: { theme: 'stripe' } }
                }
              >
                <SetupIntentForm
                  clientSecret={setupClientSecret}
                  label={setupFor === 'CARD' ? 'Card' : 'Bank Account'}
                  useCardElement={setupFor === 'CARD'}
                  disableWallets={disableWallets}
                  disabled={addFundsLoading}
                  onSuccess={(id) => void handleSetupSuccess(id)}
                  onError={handleSetupError}
                />
              </Elements>
            )}
          </>
        )}

        {addFundsResult && (
          <Alert severity={addFundsResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
            {addFundsResult.message}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={handleAddFunds}
          disabled={addFundsDisabled}
          startIcon={addFundsLoading ? <CircularProgress size={18} color="inherit" /> : <AddFundsIcon />}
        >
          {addFundsLoading ? 'Processing…' : 'Add Credits'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Auto-Replenish</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Automatically add credits when your balance falls below a threshold.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={arSettings.enabled}
              onChange={(e) => setArSettings((s) => ({ ...s, enabled: e.target.checked }))}
            />
          }
          label="Enable Auto-Replenish"
          sx={{ mb: 2, display: 'block' }}
        />

        {arSettings.enabled && (
          <>
            <Divider sx={{ mb: 2 }} />

            <TextField
              label="Replenish When Balance Falls Below ($)"
              type="number"
              fullWidth
              value={arSettings.thresholdAmount}
              onChange={(e) => setArSettings((s) => ({ ...s, thresholdAmount: parseFloat(e.target.value) || 0 }))}
              inputProps={{ min: 0, step: '1' }}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Amount to Add ($)"
              type="number"
              fullWidth
              value={arSettings.replenishAmount}
              onChange={(e) => setArSettings((s) => ({ ...s, replenishAmount: parseFloat(e.target.value) || 0 }))}
              inputProps={{ min: minimumAmount, step: '1' }}
              helperText={arSettings.replenishAmount < minimumAmount ? `Minimum is $${minimumAmount.toFixed(2)}` : undefined}
              error={arSettings.replenishAmount < minimumAmount}
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Payment Method Type</Typography>
              <ToggleButtonGroup
                value={arSettings.paymentSource}
                exclusive
                size="small"
                onChange={(_, v) => {
                  if (!v) return;
                  setArSettings((s) => ({
                    ...s,
                    paymentSource: v,
                    stripePaymentMethodId: '',
                  }));
                }}
              >
                <ToggleButton value="STRIPE_CARD">Credit / Debit Card</ToggleButton>
                <ToggleButton value="STRIPE_ACH">Bank Transfer (ACH)</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {!isDevMode && (
              <TextField
                select
                fullWidth
                label="Saved Stripe Method for Auto-Replenish"
                value={arSettings.stripePaymentMethodId || ''}
                onChange={(e) => setArSettings((s) => ({ ...s, stripePaymentMethodId: e.target.value }))}
                sx={{ mb: 2 }}
              >
                {availableForAuto.map((m) => (
                  <MenuItem key={m.paymentMethodId} value={m.paymentMethodId}>
                    {m.type === 'card'
                      ? `${m.card?.brand?.toUpperCase() || 'CARD'} •••• ${m.card?.last4 || '----'}`
                      : `${m.bankAccount?.bankName || 'Bank'} •••• ${m.bankAccount?.last4 || '----'}`}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {arSettings.lastReplenished && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Last replenished: {new Date(arSettings.lastReplenished).toLocaleString()}
              </Typography>
            )}
          </>
        )}

        {arSaveResult && (
          <Alert severity={arSaveResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
            {arSaveResult.message}
          </Alert>
        )}

        <Button
          variant="outlined"
          onClick={handleSaveAutoReplenish}
          disabled={arLoading}
          startIcon={arLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {arLoading ? 'Saving…' : 'Save Settings'}
        </Button>
      </Paper>
    </Box>
  );
};

export default AddFundsTab;
